import { AccommodationCombined, AccommodationCombinedType } from '../components/Accommodations/Accommodation.types';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useJoyPlacePricingV2PriceInfo } from '../components/AccommodationsV2/components/JoyPlacePricingV2/useJoyPlacePricingV2PriceInfo';
import { useMemo } from 'react';

const formatPrice = (valueInMinorUnits?: number, digits?: number): number => {
  if (!valueInMinorUnits) return 0;
  return digits ? valueInMinorUnits / Math.pow(10, digits) : valueInMinorUnits;
};

export type AccommodationsPrices = Readonly<{
  joyPrice?: number;
  joyPriceWins?: boolean;
  soldOutUrl?: string;
  providerPrices?: {
    [provider: string]: number; // support future dynamic providers
  };
}>;

export const useAccommodationsPrices = (accommodation?: AccommodationCombined): AccommodationsPrices | undefined => {
  const [showNewPriceComparisonUI] = useIsFeatureEnabled('enableAccommodationsPriceComparisonV2');

  // Decide whether to skip cached price fetching logic for room block and custom property
  const skipPriceFetch =
    !showNewPriceComparisonUI || accommodation?.type === AccommodationCombinedType.Roomblock || accommodation?.isCustomLinkEnabled || !accommodation?.isFromAccommodationCatalog;

  const { priceData, rates, soldOutUrl } = useJoyPlacePricingV2PriceInfo(accommodation, skipPriceFetch);

  return useMemo(() => {
    if (skipPriceFetch && soldOutUrl) return { soldOutUrl, joyPrice: undefined, joyPriceWins: undefined, providerPrices: undefined };
    if (skipPriceFetch) return;
    const joyPrice = formatPrice(priceData?.valueInMinorUnits, priceData?.digits);

    const { providerPrices, joyPriceWins } = rates.reduce<{
      providerPrices: {
        [provider: string]: number;
      };
      joyPriceWins: boolean;
    }>(
      (acc, rate) => {
        const key = rate.provider;
        if (key) {
          const formattedPrice = formatPrice(rate.priceInMinorUnits, rate.priceDigits);
          acc.providerPrices[key] = formattedPrice;

          if (formattedPrice > 0 && joyPrice >= formattedPrice) {
            acc.joyPriceWins = false;
          }
        }
        return acc;
      },
      {
        providerPrices: {},
        joyPriceWins: joyPrice > 0,
      },
    );

    return { providerPrices, joyPrice, joyPriceWins, soldOutUrl };
  }, [priceData?.valueInMinorUnits, priceData?.digits, rates, skipPriceFetch, soldOutUrl]);
};
